import { Instagram, ArrowUpRight } from "lucide-react";
import { BEAUTIX_GALLERY } from "../data/business";
import { BUSINESS } from "../data/business";

/**
 * Bento + marquee gallery of real BEAUTIX portfolio photos.
 * Two variants — "bento" (asymmetric grid) and "marquee" (horizontal scroll).
 */
export default function Gallery({ variant = "bento", items }) {
  const list = items || BEAUTIX_GALLERY;

  if (variant === "marquee") {
    // Duplicate for seamless infinite scroll
    const doubled = [...list, ...list];
    return (
      <section
        className="relative py-24 bg-white overflow-hidden"
        data-testid="gallery-marquee"
        aria-label="Nasze realizacje — stylizacja paznokci BEAUTIX Katowice"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="eyebrow mb-4">Nasze realizacje</p>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight max-w-2xl">
              Prawdziwe prace z Ligockiej —
              <br />
              <span className="italic text-[var(--beautix-pink)]">bez retuszu, bez filtrów</span>.
            </h2>
          </div>
          <a
            href={BUSINESS.social.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 text-sm font-medium link-underline shrink-0"
            data-testid="marquee-ig-link"
          >
            <Instagram className="w-4 h-4" strokeWidth={1.5} />
            Zobacz więcej @beautix_skn
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </a>
        </div>

        <div className="marquee-track">
          <div className="marquee-inner">
            {doubled.map((img, i) => (
              <figure
                key={i}
                className="marquee-item"
                data-testid={i < list.length ? `marquee-item-${i}` : undefined}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <figcaption className="marquee-caption">
                  <span>{img.tag}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>

        <style>{`
          .marquee-track {
            overflow: hidden;
            width: 100%;
            mask-image: linear-gradient(90deg, transparent, black 6%, black 94%, transparent);
          }
          .marquee-inner {
            display: flex;
            gap: 20px;
            width: max-content;
            animation: marquee 60s linear infinite;
          }
          .marquee-inner:hover { animation-play-state: paused; }
          .marquee-item {
            position: relative;
            width: 300px;
            height: 380px;
            flex: 0 0 auto;
            overflow: hidden;
            background: var(--beautix-cream);
          }
          .marquee-item img { transition: transform 0.6s ease; }
          .marquee-item:hover img { transform: scale(1.06); }
          .marquee-caption {
            position: absolute;
            top: 14px;
            left: 14px;
            background: rgba(255,255,255,0.85);
            backdrop-filter: blur(8px);
            color: var(--beautix-ink);
            font-size: 10px;
            letter-spacing: 0.24em;
            text-transform: uppercase;
            padding: 6px 12px;
            border-radius: 999px;
          }
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          @media (max-width: 768px) {
            .marquee-item { width: 240px; height: 300px; }
          }
        `}</style>
      </section>
    );
  }

  // Bento variant
  return (
    <section
      className="relative py-24 md:py-32"
      data-testid="gallery-bento"
      aria-label="Galeria realizacji BEAUTIX Katowice"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 mb-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
        <div className="lg:col-span-7">
          <p className="eyebrow mb-4">Portfolio · Ligocka 80, Katowice</p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Autorskie stylizacje,
            <br />
            <span className="italic text-[var(--beautix-pink)]">jakich nigdzie indziej</span>{" "}
            nie zobaczysz.
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p className="text-base text-[var(--beautix-muted)] font-light leading-relaxed">
            Zdjęcia poniżej to prawdziwe realizacje wykonane w salonie BEAUTIX
            w Katowicach. Od klasycznej hybrydy po autorskie zdobienia i art
            deco — wszystko z jednej pracowni na Ligockiej.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-6 auto-rows-[160px] md:auto-rows-[200px] gap-3 md:gap-4">
        {/* Large 1 */}
        <BentoTile img={list[0]} className="col-span-6 md:col-span-3 row-span-2" testId="bento-0" />
        <BentoTile img={list[1]} className="col-span-3 md:col-span-2 row-span-1" testId="bento-1" />
        <BentoTile img={list[2]} className="col-span-3 md:col-span-1 row-span-1" testId="bento-2" />
        <BentoTile img={list[3]} className="col-span-3 md:col-span-2 row-span-2" testId="bento-3" />
        <BentoTile img={list[4]} className="col-span-3 md:col-span-1 row-span-1" testId="bento-4" />
        {/* second row */}
        <BentoTile img={list[5]} className="col-span-3 md:col-span-2 row-span-1" testId="bento-5" />
        <BentoTile img={list[6]} className="col-span-3 md:col-span-2 row-span-2" testId="bento-6" />
        <BentoTile img={list[7]} className="col-span-6 md:col-span-2 row-span-1" testId="bento-7" />
        <BentoTile img={list[8]} className="col-span-3 md:col-span-2 row-span-1" testId="bento-8" />
        <BentoTile img={list[9]} className="col-span-3 md:col-span-2 row-span-1" testId="bento-9" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 mt-12 flex flex-col md:flex-row md:justify-between gap-4 text-sm">
        <p className="text-[var(--beautix-muted)]">
          Chcesz zobaczyć więcej? Śledź nas na Instagramie{" "}
          <a
            href={BUSINESS.social.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="link-underline text-[var(--beautix-pink)] font-medium"
          >
            @beautix_skn
          </a>
          {" "}oraz Facebooku{" "}
          <a
            href={BUSINESS.social.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="link-underline text-[var(--beautix-pink)] font-medium"
          >
            /najlepsze.paznokcie.w.miescie
          </a>
        </p>
      </div>
    </section>
  );
}

function BentoTile({ img, className, testId }) {
  if (!img) return null;
  return (
    <figure
      className={`relative overflow-hidden group bg-[var(--beautix-cream)] ${className}`}
      data-testid={testId}
    >
      <img
        src={img.src}
        alt={img.alt}
        loading="lazy"
        style={{
          ...(img.objectPosition ? { objectPosition: img.objectPosition } : {}),
          ...(img.objectFit ? { objectFit: img.objectFit } : {}),
        }}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
      />
      <span className="absolute top-3 left-3 z-10 bg-white/90 backdrop-blur-sm text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 rounded-full text-[var(--beautix-ink)]">
        {img.tag}
      </span>
      <span className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
    </figure>
  );
}
